import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AthleteAvatar } from '../components/athlete_avatar';
import { LoadingSkeleton, StatusPanel } from '../components/status_panel';
import {
  apiAdminDeleteResult,
  apiAdminGetResults,
  getAdminApiErrorMessage,
  type AdminDeletionReason,
  type AdminResult,
  type AdminResultsPage,
} from '../data/api';
import { formatMetric, getDisplayName } from '../lib/result_utils';

const PAGE_SIZE = 25;

export function AdminResultsPage() {
  const { getAccessTokenSilently, logout } = useAuth0();
  const [results, setResults] = useState<AdminResultsPage>();
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [retryKey, setRetryKey] = useState(0);
  const [selected, setSelected] = useState<AdminResult>();
  const [reason, setReason] = useState<AdminDeletionReason>('INCORRECT_DATA');
  const [confirmation, setConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    let isCurrent = true;
    void getAccessTokenSilently()
      .then((accessToken) =>
        apiAdminGetResults(accessToken, {
          search: search || undefined,
          page,
          pageSize: PAGE_SIZE,
        }),
      )
      .then((data) => {
        if (!isCurrent) return;
        setResults(data);
        setLoadState('ready');
      })
      .catch((loadError: unknown) => {
        if (!isCurrent) return;
        setError(
          getAdminApiErrorMessage(
            loadError,
            'The stored results could not be loaded.',
          ),
        );
        setLoadState('error');
      });
    return () => {
      isCurrent = false;
    };
  }, [getAccessTokenSilently, page, retryKey, search]);

  const closeDialog = useCallback(() => {
    if (isDeleting) return;
    setSelected(undefined);
    setReason('INCORRECT_DATA');
    setConfirmation('');
    setDeleteError('');
  }, [isDeleting]);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoadState('loading');
    setError('');
    setPage(1);
    setSearch(searchInput.trim());
    setRetryKey((current) => current + 1);
  };

  const handleDelete = async () => {
    if (!selected || confirmation.trim() !== selected.externalIdNoWithoutText) {
      return;
    }

    setIsDeleting(true);
    setDeleteError('');
    try {
      const accessToken = await getAccessTokenSilently();
      await apiAdminDeleteResult(selected.id, reason, accessToken);
      const shouldMoveBack =
        results?.items.length === 1 && page > 1;
      setSelected(undefined);
      setReason('INCORRECT_DATA');
      setConfirmation('');
      setDeleteError('');
      if (shouldMoveBack) {
        setLoadState('loading');
        setPage((current) => current - 1);
      } else {
        setLoadState('loading');
        setRetryKey((current) => current + 1);
      }
    } catch (deletionError) {
      setDeleteError(
        getAdminApiErrorMessage(
          deletionError,
          'The result could not be removed. Nothing has been reported as deleted.',
        ),
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="app-gradient min-h-[calc(100dvh-3.5rem)] px-3 py-10 sm:px-6">
      <section className="mx-auto max-w-7xl">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.2em] text-qs-indigo">
              Administration
            </p>
            <h1 className="mt-2 text-3xl font-black text-qs-navy">Manage results</h1>
            <p className="mt-2 max-w-2xl text-aws-slate">
              Review stored player results. Records cannot be edited, but they can be permanently removed.
            </p>
          </div>
          <div className="flex gap-3">
            <Link to="/admin/upload-data" className="button-primary px-4 py-2 text-sm font-black">
              Upload data
            </Link>
            <button
              type="button"
              className="rounded-lg border border-qs-indigo px-4 py-2 text-sm font-black text-qs-indigo"
              onClick={() => {
                void logout({ logoutParams: { returnTo: window.location.origin } });
              }}
            >
              Sign out
            </button>
          </div>
        </div>

        <form className="surface-card mt-7 flex flex-col gap-3 p-4 sm:flex-row" onSubmit={handleSearch}>
          <label htmlFor="admin-results-search" className="sr-only">Search stored results</label>
          <input
            id="admin-results-search"
            type="search"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search name, athlete ID, or event"
            className="input-themed min-w-0 flex-1 px-4 py-3"
          />
          <button type="submit" className="button-primary px-6 py-3 font-black">Search</button>
        </form>

        {loadState === 'loading' && (
          <div className="mt-8"><LoadingSkeleton label="Loading stored results" /></div>
        )}
        {loadState === 'error' && (
          <div className="mt-8">
            <StatusPanel
              title="Could not load stored results"
              tone="error"
              action={
                <button
                  type="button"
                  className="button-primary px-5 py-3 font-black"
                  onClick={() => {
                    setLoadState('loading');
                    setError('');
                    setRetryKey((current) => current + 1);
                  }}
                >
                  Try again
                </button>
              }
            >
              {error}
            </StatusPanel>
          </div>
        )}
        {loadState === 'ready' && results?.items.length === 0 && (
          <div className="mt-8">
            <StatusPanel title={search ? 'No matching results' : 'No stored results'}>
              {search ? 'Try another name, athlete ID, or event.' : 'Upload event data to add players.'}
            </StatusPanel>
          </div>
        )}
        {loadState === 'ready' && results && results.items.length > 0 && (
          <>
            <div className="mt-6 flex items-center justify-between text-sm font-bold text-aws-slate">
              <p>{results.total} stored {results.total === 1 ? 'result' : 'results'}</p>
              <p>Page {results.page} of {Math.max(results.totalPages, 1)}</p>
            </div>
            <div className="surface-card mt-3 overflow-x-auto">
              <table className="w-full min-w-[1050px] text-left text-xs">
                <thead className="bg-qs-navy text-white">
                  <tr>
                    <th className="px-3 py-3" scope="col">Player</th>
                    <th className="px-3 py-3" scope="col">Event</th>
                    <th className="px-3 py-3 text-center" scope="col">Rank</th>
                    <th className="px-3 py-3 text-center" scope="col">Overall</th>
                    <th className="px-3 py-3 text-center" scope="col">40 Yard</th>
                    <th className="px-3 py-3 text-center" scope="col">Broad</th>
                    <th className="px-3 py-3 text-center" scope="col">Vertical</th>
                    <th className="px-3 py-3 text-center" scope="col">Pro Agility</th>
                    <th className="px-3 py-3" scope="col">Status</th>
                    <th className="px-3 py-3 text-right" scope="col">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-indigo-100 bg-white">
                  {results.items.map((result) => (
                    <tr key={result.id} className="hover:bg-indigo-50/70">
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-3">
                          <AthleteAvatar
                            firstName={result.firstName}
                            lastName={result.lastName}
                            imageUrl={result.thumbnailImageUrl}
                            size="sm"
                          />
                          <div>
                            <p className="font-black text-qs-navy">
                              {getDisplayName(result)}
                              {result.isProfessional && <span className="ml-2 text-qs-violet">PRO</span>}
                            </p>
                            <p className="text-aws-slate">{result.externalIdNoWithoutText}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <p className="font-bold">{result.eventName}</p>
                        <p className="text-aws-slate">{result.eventDate}</p>
                      </td>
                      <td className="px-3 py-3 text-center">{result.rank ?? '-'}</td>
                      <td className="px-3 py-3 text-center">{result.overallScore ?? '-'}</td>
                      <td className="px-3 py-3 text-center">{formatMetric(result.sprint40Seconds, 'sprint40Seconds')}</td>
                      <td className="px-3 py-3 text-center">{formatMetric(result.broadJumpInches, 'broadJumpInches')}</td>
                      <td className="px-3 py-3 text-center">{formatMetric(result.verticalJumpInches, 'verticalJumpInches')}</td>
                      <td className="px-3 py-3 text-center">{formatMetric(result.proAgilitySeconds, 'proAgilitySeconds')}</td>
                      <td className="px-3 py-3 font-bold">
                        {result.deletionStatus === 'DELETING' ? 'Removal incomplete' : result.assetStatus}
                      </td>
                      <td className="px-3 py-3 text-right">
                        <button
                          type="button"
                          className="rounded-lg border border-red-600 px-3 py-2 font-black text-red-700 hover:bg-red-50"
                          onClick={() => {
                            setSelected(result);
                            setDeleteError('');
                          }}
                        >
                          {result.deletionStatus === 'DELETING' ? 'Retry removal' : 'Remove'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                disabled={page <= 1}
                className="rounded-lg border border-qs-indigo px-4 py-2 font-black text-qs-indigo disabled:opacity-40"
                onClick={() => {
                  setLoadState('loading');
                  setPage((current) => current - 1);
                }}
              >
                Previous
              </button>
              <button
                type="button"
                disabled={page >= results.totalPages}
                className="button-primary px-4 py-2 font-black disabled:opacity-40"
                onClick={() => {
                  setLoadState('loading');
                  setPage((current) => current + 1);
                }}
              >
                Next
              </button>
            </div>
          </>
        )}
      </section>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-qs-navy/70 p-4" role="presentation">
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="remove-result-title"
            className="surface-card w-full max-w-lg p-6"
          >
            <h2 id="remove-result-title" className="text-2xl font-black text-qs-navy">
              Permanently remove {getDisplayName(selected)}?
            </h2>
            <p className="mt-3 leading-6 text-aws-slate">
              This removes the database record and all associated player images and social cards from S3.
              A later upload may recreate the player.
            </p>
            {selected.isProfessional && (
              <p className="mt-3 rounded-lg bg-amber-100 p-3 font-bold text-amber-900">
                This is a professional benchmark. Removing it changes comparisons shown to players.
              </p>
            )}
            <label htmlFor="deletion-reason" className="mt-5 block font-black text-qs-navy">
              Reason
            </label>
            <select
              id="deletion-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value as AdminDeletionReason)}
              disabled={isDeleting}
              className="input-themed mt-2 w-full px-4 py-3"
            >
              <option value="INCORRECT_DATA">Incorrect data</option>
              <option value="PRIVACY">Privacy request</option>
            </select>
            <label htmlFor="deletion-confirmation" className="mt-5 block font-black text-qs-navy">
              Type athlete ID {selected.externalIdNoWithoutText} to confirm
            </label>
            <input
              id="deletion-confirmation"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              disabled={isDeleting}
              autoComplete="off"
              className="input-themed mt-2 w-full px-4 py-3"
            />
            {deleteError && <p role="alert" className="mt-4 font-bold text-red-700">{deleteError}</p>}
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                disabled={isDeleting}
                className="rounded-lg border border-qs-indigo px-4 py-2 font-black text-qs-indigo disabled:opacity-40"
                onClick={closeDialog}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isDeleting || confirmation.trim() !== selected.externalIdNoWithoutText}
                className="rounded-lg bg-red-700 px-4 py-2 font-black text-white disabled:opacity-40"
                onClick={() => {
                  void handleDelete();
                }}
              >
                {isDeleting ? 'Removing…' : 'Permanently remove'}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
